import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useT } from '../i18n/I18nContext';
import { trackEvent } from '../analytics';
import { useTrackSection } from '../hooks/useTrackSection';

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.11)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: open ? '1px solid rgba(243,107,175,0.35)' : '1px solid rgba(255,255,255,0.20)',
        borderRadius: 18,
        overflow: 'hidden',
        // Promote to its own compositing layer so backdrop-filter doesn't
        // re-rasterise the underlying canvas during the expand animation.
        transform: 'translateZ(0)',
        willChange: 'transform',
      }}
    >
      <button
        onClick={() => {
          if (!open) trackEvent('faq_opened', { question_index: index, question: q.slice(0, 60) });
          setOpen(v => !v);
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: 'clamp(16px, 2vw, 22px) clamp(18px, 2.5vw, 26px)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'Manrope,sans-serif',
        }}
      >
        <span style={{
          fontSize: 'clamp(14px, 1.5vw, 16px)',
          fontWeight: 600,
          color: 'rgba(255,255,255,0.92)',
          lineHeight: 1.4,
          flex: 1,
          minWidth: 0,
        }}>
          {q}
        </span>
        <div style={{
          width: 28,
          height: 28,
          borderRadius: 9,
          background: open ? 'rgba(243,107,175,0.20)' : 'rgba(255,255,255,0.10)',
          border: '1px solid rgba(255,255,255,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.2s ease',
        }}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{ transform: open ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
          >
            <path d="M6 1v10M1 6h10" stroke={open ? '#F36BAF' : 'rgba(255,255,255,0.7)'} strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
      </button>

      <div className={open ? 'faq-body faq-body--open' : 'faq-body'}>
        <div className="faq-inner">
          <div style={{ padding: '0 clamp(18px, 2.5vw, 26px) clamp(16px, 2vw, 22px)' }}>
            <p style={{
              fontSize: 'clamp(13px, 1.4vw, 15px)',
              color: 'rgba(255,255,255,0.60)',
              lineHeight: 1.7,
              fontWeight: 400,
            }}>
              {a}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const headRef    = useRef<HTMLDivElement>(null);
  const listRef    = useRef<HTMLDivElement>(null);
  useTrackSection('faq', sectionRef);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headRef.current,
        { opacity: 0, y: 26 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' } }
      );
      gsap.fromTo(
        listRef.current?.querySelectorAll('.faq-item') || [],
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.07, ease: 'power2.out',
          scrollTrigger: { trigger: listRef.current, start: 'top 82%' } }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="faq"
      style={{
        position: 'relative',
        padding: 'clamp(48px, 6vw, 80px) clamp(24px, 5vw, 80px)',
        zIndex: 1,
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: 780, margin: '0 auto' }}>

        {/* Heading */}
        <div ref={headRef} style={{ textAlign: 'center', marginBottom: 'clamp(28px, 3.5vw, 44px)', opacity: 0 }}>
          <h2 style={{
            fontSize: 'clamp(26px, 3.2vw, 46px)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.022em',
            color: 'white',
          }}>
            {t.faq.title}
          </h2>
        </div>

        {/* FAQ list */}
        <div
          ref={listRef}
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          {t.faq.items.map((item, i) => (
            <div key={i} className="faq-item" style={{ opacity: 0 }}>
              <FAQItem q={item.q} a={item.a} index={i} />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .faq-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.30s ease;
        }
        .faq-body--open { grid-template-rows: 1fr; }
        .faq-body > .faq-inner { min-height: 0; overflow: hidden; }
      `}</style>
    </section>
  );
}
